import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Loader2, Store, User, MapPin, Star, Package, Wrench, Coins, Send, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { imgSrc } from "@/lib/media";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import SendDeuroModal from "@/components/SendDeuroModal";

const RADII = [25, 100, 500, 0];

export default function SearchPage() {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [radius, setRadius] = useState(user?.lat ? 100 : 0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendTo, setSendTo] = useState(null);

  useEffect(() => {
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search", { params: { q, radius_km: radius } });
        setData(data);
      } catch { setData(null); }
      setLoading(false);
    }, 300);
    setParams(q ? { q } : {}, { replace: true });
    return () => clearTimeout(id);
  }, [q, radius, setParams]);

  const total = data ? data.privati.length + data.attivita.length + data.listings.length : 0;
  const noUsers = data && data.privati.length + data.attivita.length === 0;
  const radiusLabel = (r) => (r === 0 ? t("radius_any") : `${r} km`);

  return (
    <div className="px-4 sm:px-6 py-6" data-testid="search-page">
      <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">{t("search_title")}</h1>
      <p className="text-[#5f6b62] mt-1">{t("search_sub")}</p>

      <div className="mt-6 relative max-w-2xl">
        <Search className="w-5 h-5 text-[#5f6b62] absolute left-4 top-1/2 -translate-y-1/2" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="universal-search-input"
          className="deuro-input pl-12 py-4 text-lg" placeholder={t("search_placeholder")} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-[#5f6b62] mr-1">{t("radius_label")}:</span>
        {RADII.map((r) => (
          <button key={r} onClick={() => setRadius(r)} data-testid={`radius-${r}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${radius === r ? "bg-[#1e3326] text-white border-[#1e3326]" : "border-[#e5dfd3] text-[#5f6b62] hover:border-[#1e3326]"}`}>
            {radiusLabel(r)}
          </button>
        ))}
        {data && !data.has_location && <span className="text-xs text-[#92600a] bg-[#fef3c7] px-3 py-1 rounded-full">{t("no_location_hint")}</span>}
      </div>

      {loading && !data ? (
        <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#d97706]" /></div>
      ) : data && total === 0 ? (
        <div className="py-20 text-center text-[#5f6b62]" data-testid="search-empty">
          <Search className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>{t("no_results")}</p>
          {radius !== 0 && <button onClick={() => setRadius(0)} data-testid="expand-search-btn" className="mt-4 px-5 py-2.5 rounded-xl bg-[#1e3326] text-white font-semibold">{t("search_expand")}</button>}
        </div>
      ) : data && (
        <div className="mt-8 space-y-10">
          {noUsers && radius !== 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-2xl bg-[#fef3c7] border border-[#f3d98a]" data-testid="expand-hint">
              <p className="text-sm text-[#92600a]">{t("no_results")}</p>
              <button onClick={() => setRadius(0)} data-testid="expand-search-btn" className="px-4 py-2 rounded-xl bg-[#1e3326] text-white text-sm font-semibold">{t("search_expand")}</button>
            </div>
          )}
          <ResultGroup title={t("label_privati")} icon={User} color="var(--primary)" items={data.privati} testid="results-privati" t={t} navigate={navigate} me={user?.id} onSend={setSendTo} />
          <ResultGroup title={t("label_attivita")} icon={Store} color="var(--accent)" items={data.attivita} testid="results-attivita" t={t} navigate={navigate} me={user?.id} onSend={setSendTo} />
          {data.listings.length > 0 && (
            <section data-testid="results-listings">
              <h2 className="font-head text-xl font-bold flex items-center gap-2 mb-4"><Package className="w-5 h-5 text-[#5f6b62]" /> {t("listings_label")} <span className="text-[#5f6b62] font-mono text-sm">({data.listings.length})</span></h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.listings.map((l) => (
                  <button key={l.id} onClick={() => navigate(`/utente/${l.user_id}`)} data-testid="listing-result"
                    className="text-left bg-white rounded-xl border border-[#e5dfd3] p-3 flex items-center gap-3 hover:border-[#d97706] transition-all">
                    <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 overflow-hidden ${l.category === "servizio" ? "bg-[#fdeae5] text-[#d9532f]" : "bg-[#f4f0e8] text-[#1e3326]"}`}>
                      {l.image_url ? <img src={imgSrc(l.image_url)} alt="" className="w-full h-full object-cover" /> : l.category === "servizio" ? <Wrench className="w-5 h-5" /> : <Package className="w-5 h-5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm truncate">{l.title}</p>
                      <p className="text-xs text-[#5f6b62] truncate">{l.user_name} · {l.city || "—"}</p>
                    </div>
                    <span className="font-mono text-xs text-[#92600a] flex items-center gap-1"><Coins className="w-3 h-3" /> {l.price_deuro.toLocaleString("it-IT")}</span>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {sendTo && <SendDeuroModal recipient={sendTo} onClose={() => setSendTo(null)} />}
    </div>
  );
}

function ResultGroup({ title, icon: Icon, color, items, testid, t, navigate, me, onSend }) {
  if (!items.length) return null;
  return (
    <section data-testid={testid}>
      <h2 className="font-head text-xl font-bold flex items-center gap-2 mb-4"><Icon className="w-5 h-5" style={{ color }} /> {title} <span className="text-[#5f6b62] font-mono text-sm">({items.length})</span></h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((u, i) => (
          <motion.div key={u.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
            className="bg-white rounded-2xl border border-[#e5dfd3] card-shadow p-4 flex flex-col gap-3" data-testid="user-result">
            <div className="flex items-start gap-3">
              <Avatar u={u} color={color} />
              <div className="min-w-0 flex-1">
                <button onClick={() => navigate(`/utente/${u.id}`)} className="font-head font-bold text-[16px] leading-tight text-left hover:text-[#d97706] truncate block w-full" data-testid="result-name">{u.name}</button>
                <p className="text-xs text-[#5f6b62] font-mono">@{u.username}</p>
                <p className="text-xs text-[#5f6b62] flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {u.city || "—"}{u.distance_km != null && <span className="font-mono">· {u.distance_km} {t("km_away")}</span>}</p>
              </div>
              <Stars avg={u.rating_avg} count={u.rating_count} />
            </div>
            {u.category && <p className="text-xs font-semibold text-[#92600a]">{u.category}</p>}
            <Tags items={[...(u.skills || []), ...(u.services || []), ...(u.products || [])].slice(0, 6)} />
            <div className="flex gap-2 mt-auto pt-1">
              <button onClick={() => navigate(`/utente/${u.id}`)} data-testid="result-view-profile" className="flex-1 py-2 rounded-lg border border-[#e5dfd3] text-sm font-semibold hover:bg-[#f4f0e8]">{t("view_profile")}</button>
              {u.id !== me && (
                <button onClick={() => navigate(`/messaggi/${u.id}`)} data-testid="result-message" title={t("msg_write")}
                  className="px-3 py-2 rounded-lg border border-[#e5dfd3] text-[#1e3326] hover:bg-[#f4f0e8]"><MessageCircle className="w-4 h-4" /></button>
              )}
              {u.id !== me && (
                <button onClick={() => onSend(u)} data-testid="result-send-deuro" className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#d97706] text-white text-sm font-semibold hover:bg-[#c26806]"><Send className="w-3.5 h-3.5" /> {t("send")}</button>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export function Avatar({ u, color, size = "w-12 h-12 text-lg" }) {
  const bg = color || (u.user_type === "attivita" ? "var(--accent)" : "var(--primary)");
  if (u.avatar) return <img src={imgSrc(u.avatar)} alt="" className={`${size} rounded-xl object-cover shrink-0`} />;
  return <div className={`${size} rounded-xl flex items-center justify-center text-white font-head font-extrabold shrink-0`} style={{ background: bg }}>{(u.name || "?").charAt(0).toUpperCase()}</div>;
}

export function Stars({ avg = 0, count = 0, size = "w-3.5 h-3.5" }) {
  return (
    <span className="flex items-center gap-1 text-xs font-mono text-[#92600a] shrink-0" data-testid="rating-badge">
      <Star className={`${size} ${count ? "fill-[#f59e0b] text-[#f59e0b]" : "text-[#c4b9a4]"}`} /> {count ? `${avg} (${count})` : "—"}
    </span>
  );
}

export function Tags({ items = [] }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((s) => <span key={s} className="px-2.5 py-0.5 rounded-full bg-[#f4f0e8] text-[#1b241e] text-xs">{s}</span>)}
    </div>
  );
}
